import { useTheme } from '@/hooks/useTheme';

const bigStats = [
  { value: '4x', label: 'Faster lead response', accent: 'cyan' },
  { value: '87%', label: 'Reduction in manual work', accent: 'violet' },
  { value: '24/7', label: 'Autonomous operation', accent: 'cyan' },
  { value: '100%', label: 'Operational visibility', accent: 'violet' },
];

const outcomes = [
  {
    icon: 'ri-arrow-up-circle-line',
    label: 'Increased lead capture',
    detail: 'Instant response to every inbound lead across all channels, around the clock.',
    accent: 'cyan',
  },
  {
    icon: 'ri-time-line',
    label: 'Reduced manual workload',
    detail: 'Eliminate operational delays caused by repetitive, manual processes.',
    accent: 'violet',
  },
  {
    icon: 'ri-user-heart-line',
    label: 'Improved client retention',
    detail: 'Deliver consistent, timely communication that builds lasting trust.',
    accent: 'cyan',
  },
  {
    icon: 'ri-bar-chart-box-line',
    label: 'Enhanced data visibility',
    detail: 'Full operational transparency across systems, teams, and outcomes.',
    accent: 'violet',
  },
];

export default function ResultsSection() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section id="results" className="overflow-hidden transition-colors duration-500" style={{ background: isDark ? '#0E1116' : '#F5F5F7' }}>
      {/* Big stats — Apple-style full bleed */}
      <div className="relative py-28 border-t border-b transition-colors duration-500" style={{
        borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
      }}>
        {/* Background glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: isDark
              ? 'radial-gradient(ellipse at 50% 50%, rgba(34,211,238,0.04) 0%, transparent 70%)'
              : 'radial-gradient(ellipse at 50% 50%, rgba(0,113,227,0.04) 0%, transparent 70%)'
          }}
        />

        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {/* Label */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className={`w-8 h-px ${isDark ? 'bg-cyan-400/50' : 'bg-blue-500/50'}`}></div>
              <span className={`text-xs tracking-widest uppercase font-medium ${isDark ? 'text-cyan-400' : 'text-blue-500'}`}>Results</span>
              <div className={`w-8 h-px ${isDark ? 'bg-cyan-400/50' : 'bg-blue-500/50'}`}></div>
            </div>
            <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>
              From Fragmented to{' '}
              <span className="text-gradient-cyan">Controlled</span>
            </h2>
          </div>

          {/* Big number grid */}
          <div className={`grid grid-cols-2 lg:grid-cols-4 gap-px rounded-2xl overflow-hidden border mb-20 transition-colors duration-500 ${
            isDark ? 'bg-white/5 border-white/6' : 'bg-black/5 border-black/6'
          }`}>
            {bigStats.map((stat, idx) => (
              <div key={idx} className={`flex flex-col items-center justify-center py-14 px-6 transition-colors duration-300 ${
                isDark ? 'bg-[#0E1116] hover:bg-[#111827]' : 'bg-[#F5F5F7] hover:bg-white'
              }`}>
                <p className={`text-6xl md:text-7xl lg:text-8xl font-bold leading-none mb-3 tracking-tight ${
                  stat.accent === 'cyan' ? 'text-gradient-cyan' : 'text-gradient-violet'
                }`}>
                  {stat.value}
                </p>
                <p className={`text-sm text-center leading-snug ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Outcomes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-16">
            {outcomes.map((outcome, idx) => (
              <div
                key={idx}
                className={`flex items-start gap-5 p-7 rounded-2xl border transition-all duration-300 ${
                  isDark
                    ? 'border-white/6 hover:border-white/10 hover:bg-[#111827]'
                    : 'border-black/6 hover:border-black/10 hover:bg-white shadow-sm'
                }`}
              >
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl shrink-0 ${
                  outcome.accent === 'cyan'
                    ? isDark ? 'bg-cyan-400/8 border border-cyan-400/15' : 'bg-blue-500/8 border border-blue-500/15'
                    : isDark ? 'bg-violet-400/8 border border-violet-400/15' : 'bg-purple-500/8 border border-purple-500/15'
                }`}>
                  <i className={`${outcome.icon} text-base ${
                    outcome.accent === 'cyan' ? (isDark ? 'text-cyan-400' : 'text-blue-500') : (isDark ? 'text-violet-400' : 'text-purple-500')
                  }`}></i>
                </div>
                <div>
                  <h3 className={`text-base font-semibold tracking-tight mb-1.5 ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>{outcome.label}</h3>
                  <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{outcome.detail}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Positioning statement — Apple-style callout */}
          <div className={`relative rounded-2xl overflow-hidden border transition-colors duration-500 ${
            isDark ? 'border-white/6' : 'border-black/6'
          }`}>
            {/* Background image */}
            <div className="absolute inset-0">
              <img
                src="https://public.readdy.ai/ai/img_res/a2f2ea26d056ba1fd9e60a1134f95de9.jpg"
                alt=""
                className="w-full h-full object-cover object-center"
                style={{ opacity: isDark ? 1 : 0.5 }}
              />
              <div className="absolute inset-0" style={{ backgroundColor: isDark ? 'rgba(14,17,22,0.75)' : 'rgba(245,245,247,0.45)' }}></div>
            </div>

            <div className="relative z-10 flex flex-col lg:flex-row lg:items-center gap-8 p-10 lg:p-14">
              <div className="flex-1">
                <span className={`text-xs tracking-widest uppercase font-medium mb-4 block ${isDark ? 'text-violet-400' : 'text-purple-500'}`}>Positioning</span>
                <h3 className={`text-3xl md:text-4xl font-bold tracking-tight leading-snug mb-4 ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>
                  Not an AI Agency.{' '}
                  <span className="text-gradient-violet">An Operational Intelligence Firm.</span>
                </h3>
                <p className={`text-base leading-relaxed max-w-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  We operate at the intersection of structure and intelligence—designing systems that ensure nothing is missed, delayed, or lost.
                </p>
              </div>
              <div className="flex-none flex items-center gap-4">
                <div className={`px-6 py-4 rounded-xl border backdrop-blur-md text-center transition-colors duration-500 ${
                  isDark
                    ? 'border-violet-400/20 bg-[#0E1116]/60'
                    : 'border-purple-500/20 bg-white/80 shadow-lg'
                }`}>
                  <p className={`text-3xl font-bold leading-none mb-1 ${isDark ? 'text-violet-400' : 'text-purple-500'}`}>Zero</p>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>tolerance for inefficiency</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}